<template>
  <a-popup
    v-model:visible="isVisible"
    :closeable="false"
  >
    <div class="tw-w-full tw-max-w-sm tw-mx-auto tw-bg-white tw-rounded-lg tw-shadow-lg tw-relative tw-z-[9999]">
      <div class="tw-text-center tw-p-6">
      <!-- Caller Avatar -->
      <div class="tw-w-20 tw-h-20 tw-rounded-full tw-overflow-hidden tw-mx-auto tw-mb-4 tw-ring-4 tw-ring-primary-100">
        <img 
          v-if="caller?.profile_image" 
          :src="caller.profile_image" 
          :alt="caller.name"
          class="tw-w-full tw-h-full tw-object-cover"
        />
        <div v-else class="tw-w-full tw-h-full tw-bg-primary-500 tw-flex tw-items-center tw-justify-center">
          <icon icon="solar:user-bold-duotone" class="tw-text-white tw-text-2xl" />
        </div>
      </div>

      <!-- Call Info -->
      <h3 class="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-2">
        {{ caller?.name || 'Unknown User' }}
      </h3>
      
      <div class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-2">
        <icon 
          :icon="callType === 'video' ? 'solar:videocamera-bold-duotone' : 'solar:phone-calling-bold-duotone'" 
          :class="[
            'tw-text-lg',
            callType === 'video' ? 'tw-text-blue-500' : 'tw-text-green-500'
          ]" 
        />
        <span class="tw-text-gray-600">
          Incoming {{ callType === 'video' ? 'video' : 'voice' }} call
        </span>
      </div>

      <!-- Call Duration / Timer -->
      <p class="tw-text-sm tw-text-gray-500 tw-mb-6">
        {{ timeRemaining > 0 ? `${timeRemaining}s to answer` : 'Call expired' }}
      </p>

      <!-- Action Buttons -->
      <div class="tw-flex tw-items-center tw-justify-center tw-gap-4">
        <!-- Decline Button -->
        <button
          @click="declineCall"
          :disabled="processing"
          class="tw-w-14 tw-h-14 tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors"
          title="Decline call"
        >
          <icon icon="solar:phone-calling-bold-duotone" class="tw-text-xl tw-rotate-180" />
        </button>

        <!-- Accept Button -->
        <button
          @click="acceptCall"
          :disabled="processing || timeRemaining <= 0"
          class="tw-w-14 tw-h-14 tw-bg-green-500 hover:tw-bg-green-600 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          title="Accept call"
        >
          <icon 
            v-if="processing"
            icon="line-md:loading-twotone-loop" 
            class="tw-text-xl" 
          />
          <icon 
            v-else
            icon="solar:phone-calling-bold-duotone" 
            class="tw-text-xl" 
          />
        </button>
      </div>

      <!-- Processing Message -->
      <p v-if="processing" class="tw-text-sm tw-text-gray-600 tw-mt-4">
        {{ processingMessage }}
      </p>
      </div>
    </div>
  </a-popup>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, onMounted, onUnmounted, watch } from 'vue';
import videoCallsApi from '@/api/video-calls';
import messagingApi from '@/api/messages';
import useAlert from '@/composables/useAlert';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  callData: {
    type: [Object, null],
    default: () => null
  },
  caller: {
    type: [Object, null],
    default: () => null
  },
  callType: {
    type: String,
    default: 'video'
  },
  roomId: {
    type: [String, null],
    default: null
  },
  conversationId: {
    type: String,
    required: false,
    default: 'global'
  },
  expiresAt: {
    type: [String, null],
    default: null
  },
  callId: {
    type: [String, null],
    default: null
  }
});

const emit = defineEmits(['call-accepted', 'call-declined', 'call-expired', 'update:visible']);

// Reactive data
const processing = ref(false);
const processingMessage = ref('');
const timeRemaining = ref(60);

// Import alert function (call directly)
// const alert = useAlert(); // Not needed, call useAlert() directly

// Computed
const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

// Timer for call expiration
let expirationTimer = null;
let countdownTimer = null;

/**
 * Accept the incoming call
 */
const acceptCall = async () => {
  if (processing.value || timeRemaining.value <= 0 || !props.callData || !props.roomId) return;
  
  // Log call acceptance attempt
  console.log('✅ CALL ACCEPTANCE ATTEMPT:', {
    timestamp: new Date().toISOString(),
    callData: props.callData,
    roomId: props.roomId,
    callType: props.callType,
    conversationId: props.conversationId,
    caller: props.caller,
    callId: props.callId
  });
  
  try {
    processing.value = true;
    processingMessage.value = 'Accepting call...';
    
    // Step 1: Accept the call via API using call_id from metadata
    // Use the call_id from the incoming call metadata, not the message ID
    const callIdToAccept = props.callData.metadata?.call_id || props.callId;
    
    if (!callIdToAccept) {
      throw new Error('Call ID not found in call data or metadata');
    }
    
    console.log('📞 ACCEPTING CALL WITH ID:', {
      timestamp: new Date().toISOString(),
      callIdToAccept: callIdToAccept,
      messageId: props.callData.id,
      metadataCallId: props.callData.metadata?.call_id,
      propCallId: props.callId
    });
    
    const response = await videoCallsApi.calls.accept(callIdToAccept);
    
    if (!response.data?.success) {
      throw new Error('Failed to accept call');
    }
    
    // Extract call_id from the response
    const callId = response.data.data?.call_id;
    if (!callId) {
      throw new Error('Call ID not found in response');
    }
    
    // Log successful call acceptance
    console.log('✅ CALL ACCEPTED SUCCESSFULLY:', {
      timestamp: new Date().toISOString(),
      response: response.data,
      roomId: props.roomId,
      callId: callId
    });
    
    // Log URL field usage
    console.log('🔗 ROOM URL FIELD USAGE:', {
      timestamp: new Date().toISOString(),
      participant_room_url: response.data.data.participant_room_url,
      room_url: response.data.data.room_url,
      final_room_url: response.data.data.participant_room_url || response.data.data.room_url
    });
    
    processingMessage.value = 'Joining call room...';
    
    // Step 2: Join the room
    console.log('🏠 ATTEMPTING TO JOIN ROOM:', {
      timestamp: new Date().toISOString(),
      roomId: props.roomId,
      callId: callId,
      callType: props.callType
    });
    
    try {
      const joinResponse = await videoCallsApi.rooms.join(props.roomId);
      console.log('✅ ROOM JOINED SUCCESSFULLY:', {
        timestamp: new Date().toISOString(),
        roomId: props.roomId,
        joinResponse: joinResponse.data
      });
    } catch (joinError) {
      console.error('❌ ERROR JOINING ROOM:', {
        timestamp: new Date().toISOString(),
        roomId: props.roomId,
        error: joinError.message,
        response: joinError.response?.data
      });
      
      // Even if room joining fails, we should still proceed with the call
      // as the backend might have already joined us to the room
      console.warn('⚠️ Room joining failed, but proceeding with call setup...');
    }
    
    // Step 3: Send call accepted message with call_id
    console.log('💬 SENDING CALL ACCEPTED MESSAGE:', {
      timestamp: new Date().toISOString(),
      conversationId: props.conversationId,
      metadata: {
        call_type: props.callType,
        room_id: props.roomId,
        call_id: callId
      }
    });
    
    await messagingApi.messages.sendCallAccepted(props.conversationId, {
      metadata: {
        call_type: props.callType,
        room_id: props.roomId,
        call_id: callId
      }
    });
    
    // Log call accepted message sent
    console.log('💬 CALL ACCEPTED MESSAGE SENT:', {
      timestamp: new Date().toISOString(),
      conversationId: props.conversationId,
      metadata: {
        call_type: props.callType,
        room_id: props.roomId,
        call_id: callId
      }
    });
    
    // Emit success with call_id
    const emitData = {
      roomId: props.roomId,
      roomUrl: response.data.data.participant_room_url || response.data.data.room_url, // Use new participant_room_url field
      callType: props.callType,
      conversationId: props.conversationId,
      caller: props.caller,
      callId: callId,
      userRole: response.data.data.user_role || 'participant' // Include user role from API response
    };
    
    console.log('📤 EMITTING CALL-ACCEPTED EVENT:', {
      timestamp: new Date().toISOString(),
      emitData: emitData
    });
    
    emit('call-accepted', emitData);
    
    console.log('✅ CALL ACCEPTANCE PROCESS COMPLETED:', {
      timestamp: new Date().toISOString(),
      modalClosing: true,
      videoCallStateShouldBeActive: true
    });
    
    // Close modal
    isVisible.value = false;
    
  } catch (error) {
    console.error('❌ ERROR ACCEPTING CALL:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      response: error.response?.data,
      callData: props.callData,
      roomId: props.roomId,
      callId: props.callId
    });
    
    const errorMessage = error.response?.data?.message || error.message || 'Failed to accept call';
    try {
      useAlert(errorMessage, 'danger');
    } catch (alertError) {
      console.error('Alert error:', alertError);
      console.log('Error:', errorMessage);
    }
    
    // Close modal on error
    isVisible.value = false;
  } finally {
    processing.value = false;
    processingMessage.value = '';
  }
};

/**
 * Decline the incoming call
 */
const declineCall = async () => {
  if (processing.value || !props.callData) return;
  
  // Log call decline attempt
  console.log('❌ CALL DECLINE ATTEMPT:', {
    timestamp: new Date().toISOString(),
    callData: props.callData,
    roomId: props.roomId,
    callType: props.callType,
    conversationId: props.conversationId,
    caller: props.caller,
    callId: props.callId
  });
  
  try {
    processing.value = true;
    processingMessage.value = 'Declining call...';
    
    // Step 1: Reject the call via API using call_id from metadata
    const callIdToReject = props.callData.metadata?.call_id || props.callId;
    
    if (!callIdToReject) {
      throw new Error('Call ID not found in call data or metadata');
    }
    
    console.log('📞 REJECTING CALL WITH ID:', {
      timestamp: new Date().toISOString(),
      callIdToReject: callIdToReject,
      messageId: props.callData.id,
      metadataCallId: props.callData.metadata?.call_id,
      propCallId: props.callId
    });
    
    await videoCallsApi.calls.reject(callIdToReject, {
      reason: 'declined_by_user'
    });
    
    // Log successful call rejection
    console.log('❌ CALL REJECTED SUCCESSFULLY:', {
      timestamp: new Date().toISOString(),
      callId: callIdToReject,
      reason: 'declined_by_user',
      roomId: props.roomId
    });
    
    // Step 2: Send call rejected message with call_id if available
    await messagingApi.messages.sendCallRejected(props.conversationId, {
      metadata: {
        call_type: props.callType,
        reason: 'declined_by_user',
        call_id: callIdToReject
      }
    });
    
    // Log call rejected message sent
    console.log('💬 CALL REJECTED MESSAGE SENT:', {
      timestamp: new Date().toISOString(),
      conversationId: props.conversationId,
      metadata: {
        call_type: props.callType,
        reason: 'declined_by_user',
        call_id: callIdToReject
      }
    });
    
    // Emit decline event
    emit('call-declined', {
      roomId: props.roomId,
      callType: props.callType,
      conversationId: props.conversationId,
      reason: 'declined_by_user',
      callId: callIdToReject
    });
    
    // Close modal
    isVisible.value = false;
    
  } catch (error) {
    console.error('❌ ERROR DECLINING CALL:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      response: error.response?.data,
      callData: props.callData,
      callId: props.callId
    });
    
    // Still close the modal even if API call fails
    isVisible.value = false;
  } finally {
    processing.value = false;
    processingMessage.value = '';
  }
};

/**
 * Handle call expiration
 */
const handleCallExpiration = async () => {
  if (!props.callData) return;
  
  // Log call expiration
  console.log('⏰ CALL EXPIRED:', {
    timestamp: new Date().toISOString(),
    callData: props.callData,
    roomId: props.roomId,
    callType: props.callType,
    conversationId: props.conversationId,
    caller: props.caller,
    callId: props.callId,
    expiresAt: props.expiresAt
  });
  
  try {
    // Auto-reject expired call using call_id from metadata
    const callIdToReject = props.callData.metadata?.call_id || props.callId;
    
    if (!callIdToReject) {
      console.warn('⏰ No call ID found for expired call, cannot reject via API');
    } else {
      console.log('📞 REJECTING EXPIRED CALL WITH ID:', {
        timestamp: new Date().toISOString(),
        callIdToReject: callIdToReject,
        messageId: props.callData.id,
        metadataCallId: props.callData.metadata?.call_id,
        propCallId: props.callId
      });
      
      await videoCallsApi.calls.reject(callIdToReject, {
        reason: 'expired'
      });
      
      // Log successful expired call rejection
      console.log('⏰ EXPIRED CALL REJECTED SUCCESSFULLY:', {
        timestamp: new Date().toISOString(),
        callId: callIdToReject,
        reason: 'expired',
        roomId: props.roomId
      });
    }
    
    // Send missed call message with call_id if available
    await messagingApi.messages.sendMissedCall(props.conversationId, {
      metadata: {
        call_type: props.callType,
        reason: 'no_answer',
        call_id: callIdToReject || props.callId
      }
    });
    
    // Log missed call message sent
    console.log('💬 MISSED CALL MESSAGE SENT:', {
      timestamp: new Date().toISOString(),
      conversationId: props.conversationId,
      metadata: {
        call_type: props.callType,
        reason: 'no_answer',
        call_id: callIdToReject || props.callId
      }
    });
    
    emit('call-expired', {
      roomId: props.roomId,
      callType: props.callType,
      conversationId: props.conversationId,
      callId: callIdToReject || props.callId
    });
    
  } catch (error) {
    console.error('❌ ERROR HANDLING CALL EXPIRATION:', {
      timestamp: new Date().toISOString(),
      error: error.message,
      response: error.response?.data,
      callData: props.callData,
      callId: props.callId
    });
  }
  
  // Close modal
  isVisible.value = false;
};

/**
 * Start the countdown timer
 */
const startCountdownTimer = () => {
  if (!props.expiresAt) {
    timeRemaining.value = 60; // Default 60 seconds
  } else {
    const expirationTime = new Date(props.expiresAt);
    const now = new Date();
    timeRemaining.value = Math.max(0, Math.floor((expirationTime - now) / 1000));
  }
  
  countdownTimer = setInterval(() => {
    timeRemaining.value -= 1;
    
    if (timeRemaining.value <= 0) {
      clearInterval(countdownTimer);
      handleCallExpiration();
    }
  }, 1000);
};

/**
 * Clean up timers
 */
const cleanupTimers = () => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
  
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
};

// Watch for visibility changes
watch(() => props.visible, (newVisible) => {
  if (newVisible) {
    startCountdownTimer();
  } else {
    cleanupTimers();
  }
});

// Lifecycle
onMounted(() => {
  if (props.visible) {
    startCountdownTimer();
  }
});

onUnmounted(() => {
  cleanupTimers();
});
</script>

<style scoped>
/* Custom styles for the call modal */
.a-popup {
  --popup-border-radius: 16px;
}

/* Pulsing animation for incoming call */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.tw-ring-4 {
  animation: pulse 2s infinite;
}
</style>
